import { useState } from "react";
import axiosInstance from "../api";
import { useAuthStore } from "../store/useAuthStore";

const defaultErrorMessage = "Error en la operación";

const handleApiError = (error, message = defaultErrorMessage) => {
  // Maneja la sesión expirada
  const status = error?.response?.status;
  if (status === 401) {
    try {
      useAuthStore.getState().logout?.();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("Error al ejecutar logout", e);
      }
    }
    window.location.href = "/?session=expired";
  }

  const backendMessage = error?.response?.data?.message || error?.response?.data?.error || message;

  return {
    message: backendMessage,
    error: error?.response?.data?.error,
    status,
  };
};

const parseDownloadError = async (error, message = defaultErrorMessage) => {
  const responseData = error?.response?.data;

  if (typeof Blob !== "undefined" && responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      const parsedData = JSON.parse(text);

      const normalizedError = {
        ...error,
        response: {
          ...error.response,
          data: parsedData,
        },
      };

      return handleApiError(normalizedError, message);
    } catch (e) {
      // Si el Blob no contiene JSON válido, continúa con el manejo normal.
    }
  }

  return handleApiError(error, message);
};

const TIMEOUTS_BY_ENDPOINT = [
  {
    keywords: ["bulk_excel", "import_from_xlsx", "create_instrument_questionnaires", "bulk_create_from_excel", "assignments_status_report"],
    timeout: 600000,
  },
  { keywords: ["health"], timeout: 60000 },
];

const getTimeoutForEndpoint = (url) => {
  for (const { keywords, timeout } of TIMEOUTS_BY_ENDPOINT) {
    if (keywords.some((keyword) => url.includes(keyword))) {
      return timeout;
    }
  }
  return 10000;
};

export const useApi = () => {
  const tokenFromStore = useAuthStore((s) => s.session?.authenticationToken);
  const token = tokenFromStore || sessionStorage.getItem("authenticationToken") || "";

  const getAuthHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const apiGet = async (url, message, params = null) => {
    try {
      const config = {
        ...(params && { params }),
        headers: getAuthHeader(),
        // Si el endpoint es lento, aumenta el margen de tiempo de espera dependiendo del endpoint
        timeout: getTimeoutForEndpoint(url),
      };

      const response = await axiosInstance.get(url, config);

      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: handleApiError(error, message) };
    }
  };

  const apiPost = async (url, message, body = null, customHeaders = {}) => {
    try {
      const response = await axiosInstance.post(url, body, {
        headers: { ...getAuthHeader(), ...customHeaders },
        // Si el endpoint es lento, aumenta el margen de tiempo de espera dependiendo del endpoint
        timeout: getTimeoutForEndpoint(url),
      });

      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: handleApiError(error, message) };
    }
  };

  const apiPatch = async (url, message, body = null) => {
    try {
      const response = await axiosInstance.patch(url, body, {
        headers: getAuthHeader(),
        // Si el endpoint es lento, aumenta el margen de tiempo de espera dependiendo del endpoint
        timeout: getTimeoutForEndpoint(url),
      });

      return { data: response.data, error: null };
    } catch (error) {
      return { data: null, error: handleApiError(error, message) };
    }
  };

  const apiDelete = async (url, message, body = null, customHeaders = {}) => {
    try {
      const response = await axiosInstance.delete(url, {
        ...(body ? { data: body } : {}),
        headers: { ...getAuthHeader(), ...customHeaders },
        timeout: getTimeoutForEndpoint(url),
      });

      return { data: response.data ?? null, error: null };
    } catch (error) {
      return { data: null, error: handleApiError(error, message) };
    }
  };

  const apiDownload = async (url, message, options = null) => {
    const isRequestConfig =
      options &&
      typeof options === "object" &&
      ["method", "data", "params", "customHeaders", "timeout"].some((key) => Object.prototype.hasOwnProperty.call(options, key));

    const requestConfig = isRequestConfig ? options : { params: options };

    const { method = "get", data = null, params = null, customHeaders = {}, timeout } = requestConfig;

    try {
      const response = await axiosInstance.request({
        url,
        method,
        ...(params && { params }),
        ...(data !== null && { data }),
        headers: {
          ...getAuthHeader(),
          ...customHeaders,
        },
        responseType: "blob",

        // Permite definir un timeout particular o usar el configurado por endpoint.
        timeout: timeout ?? getTimeoutForEndpoint(url),
      });

      return {
        data: response.data,
        headers: response.headers,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        headers: null,
        error: await parseDownloadError(error, message),
      };
    }
  };

  return {
    apiGet,
    apiPost,
    apiPatch,
    apiDelete,
    apiDownload,
  };
};

export const createApiHook = (initialState = null) => {
  const useApiState = () => {
    const [data, setData] = useState(initialState);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const startLoading = () => {
      setLoading(true);
      setError(null);
    };

    const setResponse = ({ data: responseData, error: responseError }) => {
      setData(responseData);
      setError(responseError);
      setLoading(false);
    };

    return { data, error, loading, startLoading, setResponse };
  };

  return useApiState;
};
